import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';

import { TableModule, Table, TablePageEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmationService, MessageService } from 'primeng/api';

import { Api } from '../../../api/api';
import {
  DashboardProjectsControllerListProjectsV1$Params,
  dashboardProjectsControllerDeleteProjectV1,
} from '../../../api/functions';
import { ProjectResponseDashboardDto } from '../../../api/models/project-response-dashboard-dto';
import { HeaderService } from '../../../services/header.service';
import { ProjectService } from '../../../services/project.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip } from 'rxjs';

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.css',
})
export class ProjectsComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private readonly api = inject(Api);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);
  private readonly projectService = inject(ProjectService);

  protected readonly projects = signal<ProjectResponseDashboardDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly totalRecords = signal(0);
  protected readonly searchControl = new FormControl('');

  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  protected readonly queryParams = signal<DashboardProjectsControllerListProjectsV1$Params>({
    page: 1,
    limit: 10,
  });

  protected first = 0;
  protected readonly rowsPerPageOptions = [10, 25, 50, 100];

  constructor() {}

  ngOnInit(): void {
    // Restore preferred page size from localStorage (if previously changed by the user)
    const savedLimit = Number(localStorage.getItem('dashboard.projects.limit'));
    if (!Number.isNaN(savedLimit) && savedLimit > 0) {
      const params = this.queryParams();
      this.queryParams.set({ ...params, limit: savedLimit });
    }

    this.headerService.setTitle(this.translateService.instant('projects.title'));
    this.headerService.setSubtitle(this.translateService.instant('projects.subtitle'));

    // Load initial data
    this.loadProjects();

    // Setup search with debounce
    this.searchControl.valueChanges
      .pipe(
        skip(1), // Skip initial value emission
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        // Mark that we're searching to restore focus later
        this.isSearching = true;

        // Reset to first page when searching
        this.first = 0;
        const params = this.queryParams();
        this.queryParams.set({
          ...params,
          page: 1,
          search: searchTerm || undefined,
        });
        this.loadProjects();
      });
  }

  protected onPageChange(event: TablePageEvent): void {
    const page = Math.floor((event.first || 0) / (event.rows || this.queryParams().limit!)) + 1;
    const limit = event.rows || this.queryParams().limit!;

    this.first = event.first || 0;

    // Persist user's page size preference
    const currentLimit = this.queryParams().limit!;
    if (limit !== currentLimit) {
      localStorage.setItem('dashboard.projects.limit', String(limit));
    }

    // Preserve search term when paginating
    const currentParams = this.queryParams();
    this.queryParams.set({
      page,
      limit,
      search: currentParams.search,
    });

    this.loadProjects();
  }

  private loadProjects(): void {
    this.loading.set(true);
    this.searchControl.disable();

    this.projectService
      .loadProjectsPaginated(this.queryParams())
      .then((response) => {
        this.projects.set(response.projects || []);
        this.totalRecords.set(response.total || 0);
      })
      .catch(() => {
        this.projects.set([]);
        this.totalRecords.set(0);
      })
      .finally(() => {
        this.loading.set(false);
        this.searchControl.enable();

        // Restore focus to search input if we were searching
        if (this.isSearching && this.searchInput) {
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.isSearching = false;
          }, 0);
        }
      });
  }

  protected editProject(project: ProjectResponseDashboardDto): void {
    this.router.navigate(['/projects', project.id]);
  }

  protected addProject(): void {
    this.router.navigate(['/projects', 'new']);
  }

  protected refreshProjects(): void {
    const params = this.queryParams();
    this.queryParams.set({
      ...params,
      page: Math.floor(this.first / params.limit!) + 1,
    });
    this.loadProjects();
  }

  protected deleteProject(project: ProjectResponseDashboardDto): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('projects.confirmDelete.message', {
        name: project.name,
      }),
      header: this.translateService.instant('projects.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('projects.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('projects.confirmDelete.cancel'),
      accept: () => {
        this.api
          .invoke(dashboardProjectsControllerDeleteProjectV1, { id: project.id })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('projects.success.deleteTitle'),
              detail: this.translateService.instant('projects.success.deleteMessage', {
                name: project.name,
              }),
              life: 4000,
            });

            // Refresh both lists
            this.projectService.loadAllProjects(); // Refresh sidenav
            this.refreshProjects(); // Refresh current page
          })
          .catch(() => {});
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
